import { fetcher } from '#lib/backendFetcher';

/**
 * Fetches all email campaigns for the organization.
 * @returns {Promise<{data: Array<Object>, error: null}>}
 */
export const fetchAllEmailCampaigns = async () => {
	const data = await fetcher('/api/v1/email/campaigns/crud');
	return { data: data?.campaigns ?? [], error: null };
};

/**
 * Fetches a single email campaign by its ID.
 * @param {string} emailCampaignId - UUID of the campaign
 * @returns {Promise<{data: Object, error: null}>}
 */
export const fetchSingleEmailCampaign = async (emailCampaignId) => {
	const data = await fetcher(`/api/v1/email/campaigns/crud/${emailCampaignId}`);
	return { data: data ?? [], error: null };
};

/**
 * Returns the available campaign statuses as an array.
 * @returns {Promise<{data: Array<string>, error: null}>}
 */
export const fetchCampaignStatusEnum = async () => {
	// Hardcoded — the backend exposes no RPC for campaign statuses.
	const statuses = ['draft', 'active', 'paused', 'completed'];
	return { data: statuses, error: null };
};

/**
 * Creates a new email campaign.
 * @param {Object} params
 * @param {string} params.name - Name of the campaign
 * @param {string} [params.campaignType='template'] - Campaign type ('template' or 'ai')
 * @returns {Promise<{data: Object, error: null}>}
 */
export const createEmailCampaign = async ({ name, campaignType = 'template' }) => {
	const data = await fetcher('/api/v1/email/campaigns/crud', {
		method: 'POST',
		body: {
			campaignName: name,
			campaignType,
		},
	});
	return { data, error: null };
};

const statusToAction = (status) => {
	if (status === 'active') return 'start';
	if (status === 'paused') return 'pause';
	return null;
};

/**
 * Updates an existing email campaign (including status changes).
 * @param {Object} params
 * @param {string} params.id - UUID of the campaign
 * @param {Object} params.updates - Fields to update
 * @param {string} [params.prevStatus] - Previous status (to detect status transitions)
 * @returns {Promise<{error: null}>}
 */
export const updateEmailCampaign = async ({ id, updates, prevStatus }) => {
	const statusChanged = updates.campaignStatus && updates.campaignStatus !== prevStatus;
	const action = statusChanged ? statusToAction(updates.campaignStatus) : null;

	delete updates.campaignStatus;

	if (Object.keys(updates).length > 0) {
		await fetcher(`/api/v1/email/campaigns/crud/${id}`, {
			method: 'PATCH',
			body: updates,
		});
	}

	if (action) {
		await fetcher(`/api/v1/email/campaigns/${id}/manage`, {
			method: 'POST',
			body: { action },
		});
	}

	return { error: null };
};

/**
 * Deletes an email campaign.
 * @param {string} id - UUID of the campaign
 * @returns {Promise<{error: null}>}
 */
export const deleteEmailCampaign = async (id) => {
	await fetcher(`/api/v1/email/campaigns/crud/${id}`, {
		method: 'DELETE',
	});
	return { error: null };
};

/**
 * Links contact lists to a campaign (replaces existing associations).
 * @param {string} campaignId - UUID of the campaign
 * @param {Array<string>} listIds - Array of contact list UUIDs
 * @returns {Promise<{error: null}>}
 */
export const upsertEmailListsOfCampaign = async (campaignId, listIds) => {
	await fetcher(`/api/v1/email/campaigns/crud/${campaignId}/lists`, {
		method: 'PUT',
		body: { contactListIds: listIds },
	});
	return { error: null };
};

/**
 * Calculates aggregated KPIs across all email campaigns.
 * @returns {Promise<{data: {sent: number, delivered: number, pending: number, openRate: number, clickRate: number}, error: null}>}
 */
export const fetchCampaignKPIs = async () => {
	// Fetch all campaigns, then aggregate per-campaign KPIs
	const campaignsData = await fetcher('/api/v1/email/campaigns/crud');
	const campaigns = campaignsData?.campaigns ?? [];

	if (campaigns.length === 0) {
		return {
			data: { sent: 0, delivered: 0, pending: 0, openRate: 0, clickRate: 0 },
			error: null,
		};
	}

	const kpiResults = await Promise.all(
		campaigns.map((c) => fetcher(`/api/v1/email/campaigns/crud/${c.id}/kpis`).catch(() => null)),
	);

	let totalSent = 0;
	let totalOpened = 0;
	let totalClicked = 0;
	let totalAll = 0;

	for (const kpi of kpiResults) {
		if (!kpi) continue;
		totalAll += kpi.total ?? 0;
		totalSent += kpi.sent ?? 0;
		totalOpened += kpi.opened ?? 0;
		totalClicked += kpi.clicked ?? 0;
	}

	const openRate = totalSent > 0 ? parseFloat(((totalOpened / totalSent) * 100).toFixed(1)) : 0;
	const clickRate = totalSent > 0 ? parseFloat(((totalClicked / totalSent) * 100).toFixed(1)) : 0;

	return {
		data: {
			sent: totalSent,
			delivered: totalSent,
			pending: totalAll - totalSent,
			openRate,
			clickRate,
		},
		error: null,
	};
};

// AI Campaign Config & Generated Emails CRUD

/**
 * Creates or updates the AI configuration of a campaign.
 * @param {string} campaignId - UUID of the campaign
 * @param {Object} config - AI configuration parameters (tone of voice, prompt instructions, etc.)
 * @returns {Promise<{data: Object, error: null}>}
 */
export const upsertAIConfig = async (campaignId, config) => {
	const data = await fetcher(`/api/v1/email/campaigns/crud/${campaignId}/ai-config`, {
		method: 'PUT',
		body: config,
	});
	return { data, error: null };
};

/**
 * Fetches AI-generated emails for a campaign with pagination.
 * @param {string} campaignId - UUID of the campaign
 * @param {Object} [options]
 * @param {number} [options.page=1] - Page number
 * @param {number} [options.limit=20] - Entries per page
 * @returns {Promise<{data: Array<Object>, error: null, pagination: {page: number, limit: number, total: number, totalPages: number}}>}
 */
export const fetchGeneratedEmails = async (campaignId, { page = 1, limit = 20 } = {}) => {
	const params = new URLSearchParams({
		page: String(page),
		pageSize: String(limit),
	});

	const data = await fetcher(`/api/v1/email/campaigns/crud/${campaignId}/generated-emails?${params}`);

	return {
		data: data?.emails ?? [],
		error: null,
		pagination: {
			page: data?.currentPage ?? page,
			limit: data?.pageSize ?? limit,
			total: data?.total ?? 0,
			totalPages: data?.totalPageCount ?? 0,
		},
	};
};

/**
 * Updates the subject and/or content of an AI-generated email.
 * @param {string} emailId - UUID of the generated email
 * @param {Object} params
 * @param {string} [params.subject] - New subject
 * @param {string} [params.content] - New content
 * @param {string} params.campaignId - UUID of the associated campaign
 * @returns {Promise<{error: null}>}
 */
export const updateGeneratedEmail = async (emailId, { subject, content, campaignId }) => {
	const updates = {};
	if (subject !== undefined) updates.generatedSubject = subject;
	if (content !== undefined) updates.generatedContent = content;

	await fetcher(`/api/v1/email/campaigns/crud/${campaignId}/generated-emails/${emailId}`, {
		method: 'PATCH',
		body: updates,
	});
	return { error: null };
};

/**
 * Approves a single AI-generated email for sending.
 * @param {string} emailId - UUID of the generated email
 * @param {Object} [params]
 * @param {string} [params.campaignId] - UUID of the associated campaign
 * @returns {Promise<{error: null}>}
 */
export const approveGeneratedEmail = async (emailId, { campaignId } = {}) => {
	await fetcher(`/api/v1/email/campaigns/crud/${campaignId}/generated-emails/${emailId}/approve`, {
		method: 'POST',
	});
	return { error: null };
};

/**
 * Reverts an approved AI-generated email back to pending review.
 * Cancels a scheduled send if the campaign is already active.
 * @param {string} emailId - UUID of the generated email
 * @param {Object} [params]
 * @param {string} [params.campaignId] - UUID of the associated campaign
 * @returns {Promise<{error: null}>}
 */
export const unapproveGeneratedEmail = async (emailId, { campaignId } = {}) => {
	await fetcher(`/api/v1/email/campaigns/crud/${campaignId}/generated-emails/${emailId}/unapprove`, {
		method: 'POST',
	});
	return { error: null };
};

/**
 * Removes a recipient from a campaign.
 * @param {string} campaignId - UUID of the campaign
 * @param {string} crmContactId - UUID of the CRM contact
 * @returns {Promise<Object>}
 */
export const removeRecipientFromCampaign = async (campaignId, crmContactId) => {
	return fetcher(`/api/v1/email/campaigns/${campaignId}/recipients/${crmContactId}`, {
		method: 'DELETE',
	});
};

/**
 * Approves all AI-generated emails of a campaign at once.
 * @param {string} campaignId - UUID of the campaign
 * @returns {Promise<{error: null, approvedCount: number}>}
 */
export const approveAllGeneratedEmails = async (campaignId) => {
	const data = await fetcher(`/api/v1/email/campaigns/crud/${campaignId}/generated-emails/approve-all`, {
		method: 'POST',
	});
	return { error: null, approvedCount: data?.approvedCount ?? 0 };
};
